import QRScannerViewModel from "@/components/screens/Login/Models/QRScannerViewModel";
import type {
  ICodeSegmentsProps,
  IForgotPasswordAlertState,
  ILoginFormAction,
  ILoginViewModel,
} from "@/components/screens/Login/Types/LoginTypes";
import { LoginSection } from "@/components/screens/Login/Types/LoginTypes";
import {
  CODE_GROUP_SIZE,
  CODE_LENGTH,
  extractScannedCode,
  formatCodeForSubmit,
  isCodeComplete,
  normaliseCodeInput,
} from "@/components/screens/Login/Utils/CodeUtils";
import useAPI from "@/hooks/useAPI";
import useLoader from "@/hooks/useLoader";
import { faCheckCircle } from "@/icons/regular-icons";
import { ButtonType } from "@dinesapp/dines-ui/components/Buttons/Types/Button";
import type {
  IFormGroup,
  IFormLayoutConfig,
  IFormSectionConfig,
  IInputConfig,
} from "@dinesapp/dines-ui/components/Form/Types/Form";
import { FormLayout } from "@dinesapp/dines-ui/components/Form/Types/Form";
import { InputType } from "@dinesapp/dines-ui/components/Inputs/Types/Inputs";
import { useForm } from "@tanstack/react-form";
import { zodValidator } from "@tanstack/zod-form-adapter";
import { signIn } from "next-auth/react";
import { useRouter } from "next/router";
import { useCallback, useEffect, useRef, useState } from "react";
import { z } from "zod";

const initialValues = { username: "", password: "" };
const COPYRIGHT_YEAR = new Date().getFullYear();
const PARTNER_DASHBOARD_URL = process.env.NEXT_PUBLIC_DASHBOARD_URL ?? "";

const labelClassName =
  "text-xs font-bold uppercase tracking-[0.04em] text-white/70 mb-1.5";
const textLinkVariant = "bg-transparent text-white/70 hover:text-white";

/**
 * Interface describing the per-section configuration consumed by the login VM.
 * @interface ISectionConfig
 * @property {string} eyebrow: Uppercase tagline for the section.
 * @property {string} headingLineOne: First line of the section heading.
 * @property {string} headingLineTwo: Second line of the section heading.
 * @property {string} subtitle: Subtitle copy shown below the heading.
 * @property {Array<IInputConfig>} inputs: Form inputs rendered for the section.
 * @property {Array<ILoginFormAction>} actions: Action buttons rendered for the section.
 * @property {ILoginFormAction} [scanAction]: Optional action that opens the QR scanner (OTP only).
 * @property {ICodeSegmentsProps} [codeSegments]: Optional segmented code input rendered instead of the form (OTP only).
 */
interface ISectionConfig {
  eyebrow: string;
  headingLineOne: string;
  headingLineTwo: string;
  subtitle: string;
  inputs: Array<IInputConfig>;
  actions: Array<ILoginFormAction>;
  scanAction?: ILoginFormAction;
  codeSegments?: ICodeSegmentsProps;
}

const LoginViewModel = (): ILoginViewModel => {
  const router = useRouter();
  const { query } = router;
  const { authService } = useAPI();
  const { showLoading, hideLoading } = useLoader();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentSection, setCurrentSection] = useState<LoginSection>(
    LoginSection.Login,
  );
  const [forgotPasswordAlert, setForgotPasswordAlert] =
    useState<IForgotPasswordAlertState>({ success: false, showAlert: false });
  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isScanning, setIsScanning] = useState(false);
  const [code, setCode] = useState("");

  // Native (Enter-key) form submission runs the current section's submit;
  // kept in a ref because useForm captures onSubmit once at creation.
  const nativeSubmitRef = useRef<() => void>(() => {});
  const form = useForm({
    defaultValues: initialValues,
    onSubmit: () => {
      nativeSubmitRef.current();
    },
    validatorAdapter: zodValidator(),
  });

  const [isAutomaticallyLoggingIn, setIsAutomaticallyLoggingIn] =
    useState(false);
  useEffect(() => {
    const queryCode = Array.isArray(query.code) ? query.code[0] : query.code;
    if (queryCode && !isAutomaticallyLoggingIn) {
      setIsAutomaticallyLoggingIn(true);
      signIn("dines-otp", {
        code: formatCodeForSubmit(normaliseCodeInput(queryCode)),
      });
    }
  }, [query.code, isAutomaticallyLoggingIn]);

  const errorApplied = useRef(false);
  useEffect(() => {
    if (query.error && !errorApplied.current) {
      errorApplied.current = true;
      setErrorMessage(
        "Could not log you in. Check your details and try again.",
      );
      setIsSubmitting(false);
      setIsAutomaticallyLoggingIn(false);
      submitLockRef.current = false;
    }
  }, [query.error]);

  useEffect(() => {
    if (!forgotPasswordAlert.success || !forgotPasswordAlert.showAlert) {
      return undefined;
    }
    const timer = setTimeout(() => {
      setForgotPasswordAlert({ success: true, showAlert: false });
    }, 4000);
    return () => clearTimeout(timer);
  }, [forgotPasswordAlert.success, forgotPasswordAlert.showAlert]);

  const armAuthErrorEffect = () => {
    errorApplied.current = false;
    if (query.error) {
      router.replace(router.pathname, undefined, { shallow: true });
    }
  };

  // Synchronous reentrancy lock: isSubmitting only disables the button on the
  // NEXT render, so a double click (or any duplicated dispatch) within one
  // tick could fire a second request without this.
  const submitLockRef = useRef(false);
  const acquireSubmitLock = (): boolean => {
    if (submitLockRef.current) {
      return false;
    }
    submitLockRef.current = true;
    return true;
  };

  const changeSection = (next: LoginSection) => {
    submitLockRef.current = false;
    setErrorMessage("");
    setIsSubmitting(false);
    armAuthErrorEffect();
    form.reset();
    setCode("");
    setCurrentSection(next);
  };

  const onLoginSubmit = () => {
    if (!acquireSubmitLock()) {
      return;
    }
    setErrorMessage("");
    setIsSubmitting(true);
    armAuthErrorEffect();
    signIn("dines-credentials", {
      username: form.state.values.username,
      password: form.state.values.password,
    });
  };

  const onForgotPasswordSubmit = async () => {
    if (!acquireSubmitLock()) {
      return;
    }
    setIsSubmitting(true);
    showLoading();
    try {
      const { success, error } = await authService.forgotPassword(
        form.state.values.username,
      );
      if (success) {
        setForgotPasswordAlert({ success: true, showAlert: true });
        changeSection(LoginSection.Login);
      } else {
        setErrorMessage(error || "Could not send a reset link. Try again.");
      }
    } catch {
      setErrorMessage("Could not send a reset link. Try again.");
    } finally {
      hideLoading();
      setIsSubmitting(false);
      submitLockRef.current = false;
    }
  };

  const submitCode = (chars: string) => {
    if (!acquireSubmitLock()) {
      return;
    }
    setErrorMessage("");
    setIsSubmitting(true);
    armAuthErrorEffect();
    signIn("dines-otp", { code: formatCodeForSubmit(chars) });
  };

  const onOTPSubmit = () => {
    if (!isCodeComplete(code)) {
      setErrorMessage("Enter the full code.");
      return;
    }
    submitCode(code);
  };

  const onCodeChange = (chars: string) => {
    setErrorMessage("");
    setCode(chars);
    if (isCodeComplete(chars) && !isSubmitting) {
      submitCode(chars);
    }
  };

  const onScanClick = () => {
    if (isSubmitting) {
      return;
    }
    setErrorMessage("");
    setIsScanning(true);
  };

  const onScanCancel = useCallback(() => {
    setIsScanning(false);
  }, []);

  const onCodeScanned = (scanned: string) => {
    const chars = normaliseCodeInput(extractScannedCode(scanned));
    setIsScanning(false);
    if (isSubmitting) {
      return;
    }
    setCode(chars);
    if (isCodeComplete(chars)) {
      submitCode(chars);
    } else {
      setErrorMessage("That code was not recognised. Enter it manually.");
    }
  };

  const qrScanner = QRScannerViewModel({
    active: isScanning,
    onDetected: onCodeScanned,
    onCancel: onScanCancel,
  });

  const onBack = () => {
    changeSection(LoginSection.Login);
  };

  const onOtpEntryClick = () => {
    changeSection(LoginSection.OTP);
  };

  const usernameInput: IInputConfig = {
    id: "username",
    label: "Email",
    type: InputType.Email,
    placeholder: "you@organisation.com",
    validation: z.string().email("Enter a valid email"),
    labelClassName,
    colSpan: 1,
    onChange: (value) => form.setFieldValue("username", value),
  };

  const passwordInput: IInputConfig = {
    id: "password",
    label: "Password",
    type: InputType.Password,
    placeholder: "Enter password",
    validation: z.string().min(1, "Password is required"),
    labelClassName,
    colSpan: 1,
    onChange: (value) => form.setFieldValue("password", value),
  };

  const backAction: ILoginFormAction = {
    id: "back",
    text: "Back to sign in",
    loadingText: "Back to sign in",
    buttonType: textLinkVariant,
    isVisible: true,
    isSubmitButton: false,
    isSubmitting: false,
    onClick: onBack,
  };

  // Runs the handler only when the section's inputs parse; does NOT call
  // form.handleSubmit(), so it is safe to invoke from the form's own
  // onSubmit without recursing.
  const runIfValid = (inputs: Array<IInputConfig>, handler: () => void) => {
    const schemaEntries: { [key: string]: z.ZodTypeAny } = {};
    for (const input of inputs) {
      if (input.validation) {
        schemaEntries[input.id] = input.validation;
      }
    }
    const result = z.object(schemaEntries).safeParse(form.state.values);
    if (!result.success) {
      return;
    }
    setErrorMessage("");
    handler();
  };

  // Every submit trigger (button click, Enter key, native form submission)
  // funnels through form.handleSubmit(): TanStack surfaces field errors and,
  // when valid, calls the form's onSubmit exactly once, which dispatches to
  // the current section via nativeSubmitRef. Calling the handler here as well
  // would double-submit (two signIn navigations / two reset emails per click).
  const submitActiveSection = () => {
    form.handleSubmit();
  };

  const sectionConfig = (): ISectionConfig => {
    switch (currentSection) {
      case LoginSection.ForgotPassword: {
        const inputs = [usernameInput];
        return {
          eyebrow: "Welcome back",
          headingLineOne: "Reset your",
          headingLineTwo: "password.",
          subtitle: "Enter your account email and we'll send you a reset link.",
          inputs,
          actions: [
            {
              id: "submit",
              text: "Send reset link",
              loadingText: "Sending…",
              buttonType: ButtonType.Primary,
              isVisible: true,
              isSubmitButton: true,
              isSubmitting,
              onClick: submitActiveSection,
            },
            backAction,
          ],
        };
      }
      case LoginSection.OTP: {
        const inputs: Array<IInputConfig> = [];
        return {
          eyebrow: "Welcome back",
          headingLineOne: "One-time",
          headingLineTwo: "code.",
          subtitle: "",
          inputs,
          actions: [
            {
              id: "submit",
              text: "Verify and sign in",
              loadingText: "Verifying…",
              buttonType: ButtonType.Primary,
              isVisible: true,
              isSubmitButton: true,
              isSubmitting,
              onClick: onOTPSubmit,
            },
            backAction,
          ],
          scanAction: {
            id: "scan-qr",
            text: "Scan QR code",
            loadingText: "Scan QR code",
            buttonType: ButtonType.Secondary,
            isVisible: true,
            isSubmitButton: false,
            isSubmitting: false,
            onClick: onScanClick,
          },
          codeSegments: {
            value: code,
            onChange: onCodeChange,
            length: CODE_LENGTH,
            groupSize: CODE_GROUP_SIZE,
            ariaLabel: "Verification code",
            onSubmit: onOTPSubmit,
            autoFocus: true,
          },
        };
      }
      default: {
        const inputs = [usernameInput, passwordInput];
        return {
          eyebrow: "Welcome back",
          headingLineOne: "Organisation",
          headingLineTwo: "Login",
          subtitle:
            "Sign in to keep service moving. Your organisation is right where you left it.",
          inputs,
          actions: [
            {
              id: "submit",
              text: "Sign in",
              loadingText: "Signing in…",
              buttonType: ButtonType.Primary,
              isVisible: true,
              isSubmitButton: true,
              isSubmitting,
              onClick: submitActiveSection,
            },
          ],
        };
      }
    }
  };

  const config = sectionConfig();

  // Keep the native-submit target pointing at the visible section's action.
  // OTP renders CodeSegments instead of CustomForm, so it needs no target.
  nativeSubmitRef.current =
    currentSection === LoginSection.Login
      ? () => runIfValid([usernameInput, passwordInput], onLoginSubmit)
      : currentSection === LoginSection.ForgotPassword
        ? () => runIfValid([usernameInput], onForgotPasswordSubmit)
        : () => {};

  const groups: Array<IFormGroup> = [
    {
      id: "login-group",
      layout: FormLayout.Vertical,
      inputs: config.inputs,
      colSpan: 1,
    },
  ];

  const sections: Array<IFormSectionConfig> = [
    {
      id: "login-section",
      groups,
      layout: FormLayout.Vertical,
      renderSurface: false,
      colSpan: 1,
    },
  ];

  const layout: IFormLayoutConfig = {
    id: "root-layout",
    name: "Root Layout",
    layout: FormLayout.Vertical,
    sections,
  };

  return {
    forgotPasswordAlert,
    labels: {
      eyebrow: config.eyebrow,
      headingLineOne: config.headingLineOne,
      headingLineTwo: config.headingLineTwo,
      subtitle: config.subtitle,
      logoHref: "https://dines.co.uk",
      logoAlt: "Dines",
      logoAriaLabel: "Go to the Dines website",
    },

    isAutomaticallyLoggingIn,
    isScanning,
    qrScanner,

    otpEntry: {
      isVisible: currentSection === LoginSection.Login,
      leadingText: "Or, use a",
      linkText: "One Time Passcode",
      onClick: onOtpEntryClick,
    },

    partnerLogin: {
      isVisible:
        currentSection === LoginSection.Login && Boolean(PARTNER_DASHBOARD_URL),
      href: PARTNER_DASHBOARD_URL,
      labels: {
        title: "Running a single venue?",
        subtitle: "Go to the venue login",
        ariaLabel: "Running a single venue? Go to the venue login dashboard",
      },
    },

    formVm: {
      labels: { id: "login-form" },
      form,
      layout,
      actions: config.actions,
      scanAction: config.scanAction,
      codeSegments: config.codeSegments,
      errorMessage,
      forgotPasswordTrigger: {
        isVisible: currentSection === LoginSection.Login,
        text: "Forgot password?",
        onClick: () => changeSection(LoginSection.ForgotPassword),
      },
    },

    forgotPasswordAlertViewModel: {
      labels: { alertTitle: "A reset link has been sent to your email." },
      icon: faCheckCircle,
      dataActive: forgotPasswordAlert.success,
    },

    footerViewModel: {
      labels: {
        copyrightText: `© Dines ${COPYRIGHT_YEAR}`,
        privacyText: "Privacy",
        privacyHref: "https://dines.co.uk/privacy",
        termsText: "Terms",
        termsHref: "https://dines.co.uk/tos",
        needHelpText: "Need help?",
        chatToUsText: "Chat to us",
        chatLink: "https://dines.co.uk/chat",
      },
    },
  };
};

export default LoginViewModel;
